import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { de, type Key } from './de';
import { en } from './en';

export type { Key };
export type Lang = 'de' | 'en';
export type T = (key: Key, params?: Record<string, string | number>) => string;

const DICT: Record<Lang, Record<Key, string>> = { de, en };
const STORE = 'ft-lang';

function initial(): Lang {
  try {
    const s = localStorage.getItem(STORE);
    if (s === 'de' || s === 'en') return s;
  } catch { /* privater Modus */ }
  return navigator.language?.toLowerCase().startsWith('en') ? 'en' : 'de';
}

let current: Lang = initial();
document.documentElement.lang = current;

/** Aktuelle Sprache auch außerhalb von React (Formatierung, API-Header). */
export const getLang = (): Lang => current;

export function fill(text: string, params?: Record<string, string | number>): string {
  return params ? text.replace(/\{(\w+)\}/g, (m, k: string) => (k in params ? String(params[k]) : m)) : text;
}

export const translate: T = (key, params) => fill(DICT[current][key] ?? DICT.de[key] ?? key, params);

interface Ctx { lang: Lang; setLang: (l: Lang) => void; t: T }
const I18n = createContext<Ctx>({ lang: current, setLang: () => undefined, t: translate });

export function I18nProvider({ children }: { children: (lang: Lang) => ReactNode }) {
  const [lang, setState] = useState<Lang>(current);
  const setLang = useCallback((l: Lang) => {
    current = l;
    document.documentElement.lang = l;
    try { localStorage.setItem(STORE, l); } catch { /* egal */ }
    setState(l);
  }, []);
  const value = useMemo<Ctx>(() => ({ lang, setLang, t: (k, p) => fill(DICT[lang][k] ?? DICT.de[k] ?? k, p) }), [lang, setLang]);
  return <I18n.Provider value={value}>{children(lang)}</I18n.Provider>;
}

export const useI18n = () => useContext(I18n);
